<template>
    <header
        class="fixed top-0 left-0 right-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 z-[100] transition-all"
        :class="{ 'sm:left-64': sidebarOpen }"
    >
        <PaymentIssueBanner />
        <InvitationBanner />
        <div class="px-4 sm:px-6 lg:px-8">
            <div class="flex items-center justify-between h-16 -mb-px">
                <nav-toggle></nav-toggle>
                <div class="flex items-center gap-2">
                    <notifications></notifications>
                    <hr class="w-px h-6 bg-slate-200 dark:bg-slate-700 border-0" />
                    <div
                        class="relative inline-flex"
                        v-click-outside="closeAccountDropdown"
                    >
                        <button
                            class="inline-flex justify-center items-center group px-1 rounded"
                            aria-haspopup="true"
                            @click.prevent="accountOpen = !accountOpen"
                            :aria-expanded="accountOpen"
                        >
                            <div class="flex items-center truncate gap-1">
                                <span
                                    class="truncate text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-slate-800 dark:group-hover:text-white"
                                >
                                    {{ userData?.name }}
                                </span>
                                <svg
                                    class="w-3 h-3 shrink-0 fill-current text-slate-400 dark:text-slate-500"
                                    viewBox="0 0 12 12"
                                >
                                    <path
                                        d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z"
                                    />
                                </svg>
                            </div>
                        </button>
                        <div
                            class="origin-top-right z-[60] absolute top-full right-0 min-w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 py-1.5 rounded shadow-lg dark:shadow-slate-900/50 overflow-hidden mt-1"
                            v-show="accountOpen"
                        >
                            <ul>
                                <li>
                                    <Link
                                        class="font-medium text-sm text-navy-500 dark:text-slate-200 hover:text-orange dark:hover:text-orange flex items-center py-1 px-3"
                                        href="/settings"
                                        @click="open = false"
                                        @focus="open = true"
                                        @focusout="open = false"
                                    >
                                        Settings
                                    </Link>
                                </li>
                                <li class="lg:hidden">
                                    <Link
                                        class="font-medium text-sm text-navy-500 dark:text-slate-200 hover:text-orange dark:hover:text-orange flex items-center py-1 px-3"
                                        href="/wallet/members"
                                        @click="open = false"
                                        @focus="open = true"
                                        @focusout="open = false"
                                    >
                                        Manage Connections
                                    </Link>
                                </li>
                                <li
                                    class="px-3 py-2 border-t border-slate-100 dark:border-slate-700"
                                >
                                    <span
                                        class="block text-xs text-slate-500 dark:text-slate-400 mb-1.5"
                                    >
                                        Theme
                                    </span>
                                    <ThemeToggle :show-labels="false" />
                                </li>
                                <li
                                    class="border-t border-slate-100 dark:border-slate-700"
                                >
                                    <a
                                        href="/logout"
                                        @click.prevent="logout()"
                                        class="font-medium text-sm text-navy-500 dark:text-slate-200 hover:text-orange dark:hover:text-orange flex items-center py-1 px-3"
                                    >
                                        Sign Out
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="userData?.is_impersonating"
            class="bg-blue-200 border border-blue-300 text-blue-900 px-3 py-1 text-sm text-center z-50 relative"
        >
            You are currently viewing {{ userData.impersonation_user }}'s My10x
            account. When finished,
            <a
                href="#"
                @click.prevent="leaveImpersonation"
                class="font-bold hover:text-slate-500 underline cursor-pointer"
            >
                exit back to your account
            </a>
            .
        </div>
    </header>
</template>
<script setup>
import { usePage } from '@inertiajs/vue3';
import { useAppStore } from '@/store/app';
import Auth from '@/services/auth';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import Notifications from '@/components//notifications.vue';
import NavToggle from '@/components/nav-toggle.vue';
import EventBus from '@/services/event-bus';
import { useWalletCache } from '@/composables/useWalletCache';
import InvitationBanner from '@/components/invitation-banner.vue';
import PaymentIssueBanner from '@/components/payment-issue-banner.vue';
import ThemeToggle from '@/components/ui/theme-toggle/theme-toggle.vue';

const appStore = useAppStore();
const userData = usePage().props.userData;

const { sidebarOpen } = storeToRefs(appStore);

const accountOpen = ref(false);
const open = ref(false);

const closeAccountDropdown = () => {
    accountOpen.value = false;
};

const logout = () => {
    const logout = Auth.logout();
    logout
        .then(async (response) => {
            EventBus.emit('user-logged-out');
            localStorage.removeItem('_token');
            // MY1-2830: wipe the per-user wallet cache so it never lingers for the
            // next account on a shared browser. (The mount-time owner-change guard
            // is the backstop if this best-effort cleanup doesn't finish.)
            try {
                await useWalletCache().clearAll();
            } catch (e) {
                // ignore — non-fatal
            }
            location.href = '/login';
        })
        .catch((e) => {});
};

const leaveImpersonation = async () => {
    try {
        // Use web route with session authentication instead of API route
        const response = await window.axios.post('/leave-imposter-api');

        if (response.data.success) {
            // Impersonation is session-based: leaving swaps the authenticated
            // user back to the admin server-side, so there's no bearer token to
            // restore in the browser (SUPPORT-663 / F-024, F-095).
            const redirectUrl = response.data.redirect_url || '/admin/users';
            if (response.data.force_reload) {
                window.location.href = redirectUrl;
            } else {
                router.visit(redirectUrl, {
                    method: 'get',
                    replace: true,
                });
            }
        }
    } catch (error) {
        console.error('Failed to leave impersonation:', error);
        // Fallback to full page reload
        window.location.href = '/admin/users';
    }
};
</script>
